// A row chooser for the wizard: an action's select_grid rows, an `ask` line
// source, several records matching a `load`. Several at once reuse the shared
// SelectGridModal (the same tick-and-apply grid every action uses); one at a
// time is a plain list.

import { useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import SelectGridModal, { humanizeKey } from '../../txn/SelectGridModal'
import { tw } from '../../testHooks'
import type { ChooseRequest } from '../ops'
import type { Row } from '../model'
import { T, button, dialog, overlay, table, td, th } from '../styles'

export default function ChooseDialog({ title, rows, columns, multi, labelOf, onDone }: ChooseRequest & {
  onDone: (rows: Row[] | null) => void
}) {
  const cols = useMemo(() => {
    if (columns?.length) return columns
    const seen: string[] = []
    for (const r of rows) for (const k of Object.keys(r || {})) {
      if (!k.startsWith('_') && !seen.includes(k)) seen.push(k)
    }
    return seen.slice(0, 6)
  }, [rows, columns])
  const [pick, setPick] = useState<number | null>(null)
  if (multi) {
    const labels: Record<string, string> = {}
    if (labelOf) for (const c of cols) labels[c] = labelOf(c)
    return (
      <SelectGridModal title={title} rows={rows} columns={cols} labels={labels}
        onCancel={() => onDone(null)} onApply={(sel) => onDone(sel)} />
    )
  }
  const body = (
    <div style={overlay} onClick={() => onDone(null)}>
      <div style={dialog} onClick={e => e.stopPropagation()} data-tw-dialog="wizard_choose">
        <div style={{ fontWeight: 600, fontSize: 17 }}>{title}</div>
        <div style={{ overflow: 'auto', maxHeight: '55vh', border: `1px solid ${T.border}`, borderRadius: 8 }}>
          <table style={table}>
            <thead><tr>{cols.map(c => <th key={c} style={th}>{labelOf ? labelOf(c) : humanizeKey(c)}</th>)}</tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} {...tw('row', i)} onClick={() => setPick(i)} onDoubleClick={() => onDone([r])}
                  style={{ cursor: 'pointer', background: pick === i ? 'var(--color-accent-soft, #e8f0fe)' : undefined }}>
                  {cols.map(c => <td key={c} style={td}>{r[c] == null ? '' : String(r[c])}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" style={button(false)} onClick={() => onDone(null)} {...tw('action', 'cancel')}>Cancel</button>
          <button type="button" style={button(false, true, pick === null)} disabled={pick === null}
            onClick={() => pick !== null && onDone([rows[pick]])} {...tw('action', 'apply')}>Choose</button>
        </div>
      </div>
    </div>
  )
  return typeof document !== 'undefined' ? createPortal(body, document.body) : body
}
